import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart } from 'lucide-react';

const MushroomCollection = () => {
  const mushrooms = [
    {
      name: 'Fresh Oyster Mushrooms',
      description: 'Velvety texture, harvested daily. These premium oysters are known for their delicate flavor and meaty texture, perfect for gourmet cooking.',
      color: 'from-amber-200 to-amber-400',
      accent: 'amber',
    },
    {
      name: 'Button Mushrooms',
      description: 'Farm-fresh, firm, and nutrient-dense. Our button mushrooms are cultivated to perfection, offering a classic taste and versatile culinary applications.',
      color: 'from-stone-300 to-stone-500',
      accent: 'stone',
    },
    {
      name: 'Milky Mushrooms',
      description: 'Native tropical variety, robust and meaty. These unique mushrooms thrive in our controlled environment, delivering exceptional flavor and texture.',
      color: 'from-forest-300 to-forest-500',
      accent: 'forest',
    },
    {
      name: 'Medicinal Varieties',
      description: 'Premium medicinal mushrooms including Ganoderma and Lion\'s Mane. Grown with precision for maximum bioactive compounds and health benefits.',
      color: 'from-earth-300 to-earth-500',
      accent: 'earth',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section id="varieties" className="py-24 bg-gradient-to-b from-stone-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">
            The Mushroom Collection
          </h2>
          <div className="w-24 h-1 bg-forest-600 mx-auto mb-6"></div>
          <p className="text-xl text-stone-600 max-w-3xl mx-auto">
            Discover our premium selection of organic mushrooms, each variety carefully cultivated for exceptional quality and flavor.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          {mushrooms.map((mushroom, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{ y: -10, scale: 1.02 }}
              className="group relative glass rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
            >
              {/* Visual Placeholder */}
              <div className={`h-64 bg-gradient-to-br ${mushroom.color} relative overflow-hidden`}>
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg
                    width="120"
                    height="120"
                    viewBox="0 0 120 120"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="opacity-30"
                  >
                    <path
                      d="M60 20C60 20 40 30 40 60C40 70 45 75 50 80C55 85 60 90 60 90C60 90 65 85 70 80C75 75 80 70 80 60C80 30 60 20 60 20Z"
                      fill="white"
                    />
                    <circle cx="60" cy="60" r="8" fill="currentColor" />
                  </svg>
                </div>
                {/* Decorative dots */}
                <div className="absolute top-4 right-4 w-3 h-3 bg-white/50 rounded-full"></div>
                <div className="absolute bottom-4 left-4 w-2 h-2 bg-white/50 rounded-full"></div>
                <div className="absolute top-1/2 right-8 w-2 h-2 bg-white/50 rounded-full"></div>
              </div>

              <div className="p-8">
                <h3 className="text-2xl font-bold text-stone-900 mb-4">
                  {mushroom.name}
                </h3>
                <p className="text-stone-600 mb-6 leading-relaxed">
                  {mushroom.description}
                </p>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="w-full bg-forest-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-forest-700 transition-colors flex items-center justify-center gap-2 shadow-lg"
                >
                  <ShoppingCart className="w-5 h-5" />
                  Quick Order
                </motion.button>
              </div>

              {/* Hover effect overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-forest-600/0 to-forest-600/0 group-hover:from-forest-600/5 group-hover:to-forest-600/10 transition-all duration-300 pointer-events-none rounded-3xl"></div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default MushroomCollection;
